import React, { useState } from "react";
import axios from "axios";
import { Mail, Phone, MapPin, ArrowUpRight, Check, Loader2 } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { COMPANY, WHATSAPP_URL } from "../lib/content";
import { toast, Toaster } from "sonner";

const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL?.replace(/\/+$/, "") || "";
const API = BACKEND_BASE ? `${BACKEND_BASE}/api` : "/api";

const TIPOS_OBRA = [
  "Obras Industriais",
  "Reformas Industriais",
  "Obras Comerciais",
  "Adequações Estruturais",
  "Obras Residenciais",
  "Gerenciamento de Obras",
  "Outro",
];

export default function Contato() {
  const [form, setForm] = useState({
    nome: "",
    email: "",
    telefone: "",
    empresa: "",
    tipo_obra: "",
    cidade: "",
    area_aproximada: "",
    investimento_estimado: "",
    prazo_previsto: "",
    necessidades: "",
    aceite_lgpd: false,
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: val }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.aceite_lgpd) {
      toast.error("Aceite a Política de Privacidade para continuar.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Solicitação recebida! Nossa equipe entrará em contato.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Não foi possível enviar agora. Tente o WhatsApp ou e-mail.";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full bg-transparent border-b border-sca-black/20 rounded-none px-0 py-4 text-base text-sca-black placeholder:text-sca-steel/70 focus:outline-none focus:border-sca-red transition-colors";

  return (
    <>
      <Toaster position="top-center" richColors closeButton />

      <section className="pt-28 md:pt-36 pb-10 bg-white">
        <div className="sca-container">
          <Reveal>
            <p className="sca-overline mb-6">[ Contato ]</p>
            <h1 className="sca-h1 max-w-[20ch]">
              Solicite uma <span className="sca-serif text-sca-red">avaliação técnica</span> da sua obra.
            </h1>
            <p className="sca-body mt-8 max-w-2xl">
              Envie as informações abaixo. Nossa equipe técnica retornará para entender o escopo, condições
              operacionais e estruturar a melhor abordagem para o seu projeto.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="sca-section pt-12 bg-white" data-testid="contact-section">
        <div className="sca-container grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Form */}
          <div className="lg:col-span-7" data-testid="contact-form-container">
            {submitted ? (
              <Reveal>
                <div className="border border-sca-line p-10 md:p-14 bg-sca-mist">
                  <div className="w-14 h-14 bg-sca-red text-white flex items-center justify-center mb-7">
                    <Check className="w-7 h-7" strokeWidth={2.5} />
                  </div>
                  <h2 className="sca-h3 mb-4">Solicitação recebida.</h2>
                  <p className="sca-body mb-8">
                    Obrigado, {form.nome.split(" ")[0] || "cliente"}. Nossa equipe técnica entrará em contato em breve
                    pelo telefone <strong>{form.telefone}</strong> ou e-mail <strong>{form.email}</strong>.
                  </p>
                  <a
                    href={WHATSAPP_URL(`Olá, acabei de enviar o formulário no site. Meu nome é ${form.nome}.`)}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-sca-primary"
                  >
                    Acelerar via WhatsApp <ArrowUpRight className="w-4 h-4" />
                  </a>
                </div>
              </Reveal>
            ) : (
              <form onSubmit={onSubmit} className="space-y-8" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                  <div>
                    <label className="sca-overline text-sca-steel">Nome completo *</label>
                    <input
                      required
                      value={form.nome}
                      onChange={update("nome")}
                      className={inputClass}
                      placeholder="Como podemos te chamar"
                      data-testid="contact-input-nome"
                    />
                  </div>
                  <div>
                    <label className="sca-overline text-sca-steel">Empresa</label>
                    <input
                      value={form.empresa}
                      onChange={update("empresa")}
                      className={inputClass}
                      placeholder="Nome da empresa"
                      data-testid="contact-input-empresa"
                    />
                  </div>
                  <div>
                    <label className="sca-overline text-sca-steel">E-mail *</label>
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      className={inputClass}
                      placeholder="seuemail@empresa.com.br"
                      data-testid="contact-input-email"
                    />
                  </div>
                  <div>
                    <label className="sca-overline text-sca-steel">Telefone / WhatsApp *</label>
                    <input
                      required
                      value={form.telefone}
                      onChange={update("telefone")}
                      className={inputClass}
                      placeholder="(00) 00000-0000"
                      data-testid="contact-input-telefone"
                    />
                  </div>
                  <div>
                    <label className="sca-overline text-sca-steel">Tipo de obra</label>
                    <select
                      value={form.tipo_obra}
                      onChange={update("tipo_obra")}
                      className={`${inputClass} appearance-none cursor-pointer`}
                      data-testid="contact-select-tipo"
                    >
                      <option value="">Selecione</option>
                      {TIPOS_OBRA.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="sca-overline text-sca-steel">Cidade / UF</label>
                    <input
                      value={form.cidade}
                      onChange={update("cidade")}
                      className={inputClass}
                      placeholder="Ex: Piracicaba/SP"
                      data-testid="contact-input-cidade"
                    />
                  </div>
                  <div>
                    <label className="sca-overline text-sca-steel">Área aproximada</label>
                    <input
                      value={form.area_aproximada}
                      onChange={update("area_aproximada")}
                      className={inputClass}
                      placeholder="Ex: 2.500 m²"
                      data-testid="contact-input-area"
                    />
                  </div>
                  <div>
                    <label className="sca-overline text-sca-steel">Investimento estimado</label>
                    <input
                      value={form.investimento_estimado}
                      onChange={update("investimento_estimado")}
                      className={inputClass}
                      placeholder="Ex: R$ 5 milhões"
                      data-testid="contact-input-investimento"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="sca-overline text-sca-steel">Prazo previsto</label>
                    <input
                      value={form.prazo_previsto}
                      onChange={update("prazo_previsto")}
                      className={inputClass}
                      placeholder="Ex: início em 02/2026 — entrega em 11/2026"
                      data-testid="contact-input-prazo"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="sca-overline text-sca-steel">Principais necessidades</label>
                    <textarea
                      rows={5}
                      value={form.necessidades}
                      onChange={update("necessidades")}
                      className={`${inputClass} resize-none`}
                      placeholder="Conte um pouco sobre o escopo, condições operacionais e expectativas..."
                      data-testid="contact-textarea-necessidades"
                    />
                  </div>
                </div>

                <label className="flex items-start gap-3 text-sm text-sca-steel cursor-pointer" data-testid="contact-lgpd-label">
                  <input
                    type="checkbox"
                    checked={form.aceite_lgpd}
                    onChange={update("aceite_lgpd")}
                    className="mt-1 w-4 h-4 accent-sca-red"
                    data-testid="contact-checkbox-lgpd"
                  />
                  <span>
                    Li e concordo com a <a href="/politica-de-privacidade" className="underline hover:text-sca-red">Política de Privacidade</a> e
                    autorizo o tratamento dos meus dados conforme a <a href="/lgpd" className="underline hover:text-sca-red">LGPD</a>.
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-sca-primary disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="contact-submit-button"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Solicitar avaliação técnica <ArrowUpRight className="w-4 h-4" /></>}
                </button>
              </form>
            )}
          </div>

          {/* Info */}
          <aside className="lg:col-span-5 lg:pl-10 lg:border-l border-sca-line">
            <Reveal>
              <p className="sca-overline mb-6">[ Contato direto ]</p>
              <h2 className="sca-h3 mb-10 leading-tight">
                Prefere falar com um especialista <span className="sca-serif text-sca-red">agora</span>?
              </h2>

              <ul className="space-y-7">
                <li className="flex gap-4">
                  <span className="w-11 h-11 bg-sca-black text-white inline-flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4" />
                  </span>
                  <div>
                    <p className="sca-overline mb-2 text-sca-steel">Endereço</p>
                    <p className="font-sans text-base text-sca-black leading-relaxed">
                      {COMPANY.address.line1}, {COMPANY.address.line2}<br />
                      {COMPANY.address.city} — CEP {COMPANY.address.zip}
                    </p>
                  </div>
                </li>
                <li className="flex gap-4">
                  <span className="w-11 h-11 bg-sca-black text-white inline-flex items-center justify-center flex-shrink-0">
                    <Phone className="w-4 h-4" />
                  </span>
                  <div>
                    <p className="sca-overline mb-2 text-sca-steel">Telefone / WhatsApp</p>
                    <a href={`tel:${COMPANY.phoneRaw}`} className="font-sans text-base text-sca-black hover:text-sca-red block">{COMPANY.phone}</a>
                    <a href={WHATSAPP_URL()} target="_blank" rel="noreferrer" className="font-sans text-base text-sca-black hover:text-sca-red block">{COMPANY.whatsapp}</a>
                  </div>
                </li>
                <li className="flex gap-4">
                  <span className="w-11 h-11 bg-sca-black text-white inline-flex items-center justify-center flex-shrink-0">
                    <Mail className="w-4 h-4" />
                  </span>
                  <div>
                    <p className="sca-overline mb-2 text-sca-steel">E-mail</p>
                    <a href={`mailto:${COMPANY.email}`} className="font-sans text-base text-sca-black hover:text-sca-red break-all">{COMPANY.email}</a>
                  </div>
                </li>
              </ul>

              <div className="mt-12 border-t border-sca-line pt-8">
                <p className="sca-overline mb-4">[ Horário ]</p>
                <p className="font-sans text-base text-sca-black">{COMPANY.hours}</p>
              </div>
            </Reveal>
          </aside>
        </div>
      </section>
    </>
  );
}
